import * as React from 'react';

import {StudentTheme} from '../studentTheme';
import {ModoTimer, PRESETS, calcularRestante, formatarTempo, rotuloPreset, segundosDecorridos, terminou} from '../timer';

interface Props {
  theme: StudentTheme;
}

export const TimerScreen: React.FC<Props> = ({theme}) => {
  const [modo, setModo] = React.useState<ModoTimer>('regressivo');
  const [alvo, setAlvo] = React.useState<number>(300);
  const [decorrido, setDecorrido] = React.useState(0);
  const [rodando, setRodando] = React.useState(false);

  // Guarda o instante de inicio em vez de somar 1 a cada tick: setInterval
  // acumula atraso quando a aba vai para segundo plano, e o timer voltaria
  // atrasado. Contando pelo relogio, ele se corrige sozinho.
  const inicioRef = React.useRef<number | null>(null);
  const baseRef = React.useRef(0);

  React.useEffect(() => {
    if (!rodando) return undefined;

    inicioRef.current = Date.now();

    const id = setInterval(() => {
      const desdeOInicio = segundosDecorridos(inicioRef.current ?? Date.now(), Date.now());
      setDecorrido(baseRef.current + desdeOInicio);
    }, 250);

    return () => clearInterval(id);
  }, [rodando]);

  React.useEffect(() => {
    if (rodando && terminou(modo, alvo, decorrido)) setRodando(false);
  }, [rodando, modo, alvo, decorrido]);

  const alternar = () => {
    if (rodando) {
      baseRef.current = decorrido;
      setRodando(false);
      return;
    }

    // Reiniciar apos terminar nao pode retomar do valor cheio.
    if (terminou(modo, alvo, decorrido)) {
      baseRef.current = 0;
      setDecorrido(0);
    } else {
      baseRef.current = decorrido;
    }

    setRodando(true);
  };

  const zerar = () => {
    setRodando(false);
    baseRef.current = 0;
    setDecorrido(0);
  };

  const mostrado = modo === 'regressivo' ? calcularRestante(alvo, decorrido) : decorrido;
  const acabou = terminou(modo, alvo, decorrido);

  return (
    <div className="timer">
      <div className="timer__modos" role="group" aria-label="Modo do timer">
        {(['regressivo', 'progressivo'] as ModoTimer[]).map((opcao) => (
          <button
            key={opcao}
            type="button"
            className="timer__modo"
            aria-pressed={modo === opcao}
            style={{
              borderColor: theme.border,
              backgroundColor: modo === opcao ? theme.primary : 'transparent',
              color: modo === opcao ? theme.primaryText : theme.text,
            }}
            onClick={() => {
              setModo(opcao);
              zerar();
            }}
          >
            {opcao === 'regressivo' ? 'Regressivo' : 'Progressivo'}
          </button>
        ))}
      </div>

      <output className="timer__display" style={{color: acabou ? theme.primary : theme.text}}>
        {formatarTempo(mostrado)}
      </output>

      {modo === 'regressivo' && (
        <div className="timer__presets">
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className="timer__preset"
              aria-pressed={alvo === preset}
              style={{
                borderColor: alvo === preset ? theme.primary : theme.border,
                color: alvo === preset ? theme.primary : theme.neutral,
              }}
              onClick={() => {
                setAlvo(preset);
                zerar();
              }}
              disabled={rodando}
            >
              {rotuloPreset(preset)}
            </button>
          ))}
        </div>
      )}

      <div className="timer__acoes">
        <button
          type="button"
          className="timer__acao timer__acao--principal"
          style={{backgroundColor: theme.primary, color: theme.primaryText}}
          onClick={alternar}
        >
          <i className={`fa-solid ${rodando ? 'fa-pause' : 'fa-play'}`} aria-hidden="true" />
          {rodando ? 'Pausar' : 'Iniciar'}
        </button>

        <button
          type="button"
          className="timer__acao"
          style={{borderColor: theme.border, color: theme.text}}
          onClick={zerar}
        >
          <i className="fa-light fa-rotate-left" aria-hidden="true" />
          Zerar
        </button>
      </div>
    </div>
  );
};
